import { applyMiddleware, createStore } from "redux";
import createSagaMiddleware from "redux-saga";

import { buildRootReducer } from "./build-root-reducer";
import { buildRootSaga } from "./build-root-saga";
import { globalReducers } from "./global-reducers";
import { globalPageReducers } from "./ui-state";

/**
 * Function to build the Redux application store.
 *
 * @param {Object} [options] - an optional store configuration object.
 * @param {Object} [options.appReducers=null] - An object of app state reducers.
 * @param {Object} [options.pageReducers=null] - An object of UI state reducers.
 * @param {generator[]} [options.appSagas=[]] - An array of app saga generators.
 * @param {generator[]} [options.pageSagas=[]] - An array of UI saga generators.
 * @param {Boolean} [options.debug=false] - A flag to expose Redux devtools.
 *
 * @return {Object} - Returns the application store.
 */
function buildApplicationStore(options = {}) {
  const {
    appReducers = null,
    pageReducers = null,
    appSagas = [],
    pageSagas = [],
    debug = false,
  } = options;
  const rootSaga = buildRootSaga(appSagas, pageSagas);
  const sagaMiddleware = createSagaMiddleware();

  const middlewares = [
    debug &&
      window.__REDUX_DEVTOOLS_EXTENSION__ &&
      window.__REDUX_DEVTOOLS_EXTENSION__({
        name: `${window.location.host} (${Date.now()})`,
      }),
    applyMiddleware(sagaMiddleware),
  ].filter((m) => m);

  const allPageReducers = {
    ...pageReducers,
    ...globalPageReducers,
  };

  const store = createStore(
    buildRootReducer(globalReducers, appReducers, allPageReducers),
    ...middlewares,
  );

  sagaMiddleware.run(rootSaga);

  return store;
}

export { buildApplicationStore };
